import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { Input } from 'react-daisyui';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/Spinner';

export interface SearchInputOptions {
  value: string | number;
  label: string | number;
}

interface SearchInputProps {
  value: string;
  onChange: (query: string) => void;
  placeholder?: string;
  'data-testid'?: string;
  className?: string;
  autocomplateOptions?: SearchInputOptions[];
  noResultsMessage?: string;
  typeahead?: boolean;
  onOptionSelect?: (option: string | number) => void;
  loading?: boolean;
  maxResults?: number;
  disabled?: boolean;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  'data-testid': testId = 'search-input',
  className = '',
  typeahead = false,
  autocomplateOptions,
  noResultsMessage = 'No results found',
  onOptionSelect,
  loading = false,
  maxResults = 10,
  disabled = false,
}: SearchInputProps) {
  // Controls the typeahead dropdown. Kept separate from the input's focus so
  // that closing it on select doesn't wedge it shut: picking an option leaves
  // DOM focus on the input (the option anchor isn't focusable), so no later
  // focus event would ever reopen it — the menu stayed closed for every
  // subsequent clear/type cycle. Typing reopens it directly instead.
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [slicedAutocomplateOptions, setSlicedAutocomplateOptions] =
    useState<SearchInputOptions[]>();
  const blurTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setSlicedAutocomplateOptions(autocomplateOptions?.slice(0, maxResults));
  }, [autocomplateOptions, maxResults]);

  const openMenu = () => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
    setIsOpen(true);
  };

  const onSelectItem = (option: SearchInputOptions) => {
    onChange(String(option.label));
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onOptionSelect && onOptionSelect(option.value);
    setIsOpen(false);
  };

  const replaceStringForBold = (text: string) => {
    const re = new RegExp(`\\${value}`, 'gi');
    return text.replace(
      re,
      (match) => `<span class="font-bold">${match}</span>`
    );
  };

  const handleFocus = () => {
    openMenu();
  };

  // Defer the close so a click on an option registers first (blur fires before
  // the option's click). Cancelable and short — the old 2s timer was never
  // cleared, so a stale one could collapse the menu mid-interaction.
  const handleBlur = () => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
    blurTimeout.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(
    () => () => {
      if (blurTimeout.current) clearTimeout(blurTimeout.current);
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (slicedAutocomplateOptions && slicedAutocomplateOptions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prevIndex) =>
            prevIndex === slicedAutocomplateOptions.length - 1
              ? 0
              : prevIndex + 1
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prevIndex) =>
            prevIndex === 0
              ? slicedAutocomplateOptions.length - 1
              : prevIndex - 1
          );
          break;
        case 'Enter':
          if (highlightedIndex !== -1) {
            e.preventDefault();
            const selectedOption = slicedAutocomplateOptions[highlightedIndex];
            onSelectItem(selectedOption);
          }
          break;
        default:
          break;
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className='mt-2 mb-1'>
        <div className='relative text-gray-600 focus-within:text-gray-400'>
          <span className='absolute inset-y-0 left-[263px] flex items-center pl-2'>
            {loading ? (
              <Spinner className='mx-auto w-4 text-base ml-4' />
            ) : (
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            )}
          </span>
          <Input
            data-testid={testId}
            size='sm'
            className='border-accent w-[300px]'
            placeholder={placeholder}
            type='search'
            value={value}
            onChange={(e) => {
              // Reopen on typing so the menu comes back after a select closed
              // it, even though the input never lost/regained focus.
              openMenu();
              onChange(e.target.value ?? '');
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
        </div>
      </div>
      {typeahead && onOptionSelect && (
        <ul className='menu bg-base-100 absolute z-10 min-w-[300px] w-max'>
          {slicedAutocomplateOptions &&
            value &&
            isOpen &&
            slicedAutocomplateOptions?.map((option, index) => (
              <li
                key={option.value}
                className={
                  index === highlightedIndex ? 'bg-gray-700 text-[#00A089]' : ''
                }
              >
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
                <a role='button' onClick={() => onSelectItem(option)}>
                  <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: replaceStringForBold(String(option.label)),
                    }}
                  />
                </a>
              </li>
            ))}
        </ul>
      )}
      {typeahead &&
      value &&
      autocomplateOptions?.length === 0 &&
      !disabled &&
      isOpen ? (
        <ul className='menu bg-base-100 absolute w-full z-10'>
          <li>{noResultsMessage}</li>
        </ul>
      ) : null}
    </div>
  );
}
export default SearchInput;
